"use client";

import clsx from "clsx";
import {useLayoutEffect, useRef, useState} from "react";
import {AnimatePresence, motion} from "framer-motion";
import {useTranslation} from "react-i18next";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import Icon, {ICON_TYPE} from "@/components/library/Icon";
import type {TokenBreakdown} from "@/utils/chat/tokenEstimation";
import type {Assistant} from "../AssistantSelector";
import ContextWheel from "../ContextWheel";
import {VARIANT_CLASSES, CONTROLS_CLASSES, adjustTextareaHeight, countTextareaRows} from "./constants";
import type {ChatInputDictationApi, ComposerTextareaProps} from "./types";
import MemoryComposerButton from "../memory/MemoryComposerButton";
import AssistantModelControls from "./AssistantModelControls";
import DictationButton from "./DictationButton";
import HighlightedTextarea from "./HighlightedTextarea";
import SendOrStopButton from "./SendOrStopButton";

type DesktopComposerLayoutProps = Readonly<{
   variant: "large" | "compact";
   textarea: ComposerTextareaProps;
   upload: {
      show: boolean;
      animating: boolean;
      onExitComplete: () => void;
      onClick: () => void;
      disabled: boolean;
      hasAttachedFiles: boolean;
   };
   tools: {
      hasToolsContent: boolean;
      shouldShowContextWheel: boolean;
      displayedTokenUsagePercent: number;
      tokenLimitReached: boolean;
      tokenBreakdown?: TokenBreakdown;
      showAssistantSelector: boolean;
      assistants: Assistant[];
      selectedAssistant?: Assistant;
      onAssistantChange?: (assistant: Assistant) => void;
      assistantSelectorDisabled: boolean;
      isLoadingAssistants: boolean;
      showModelSelector: boolean;
      onModelChange?: (assistantId: string, newModel: string) => void;
      canEditModel?: boolean;
      isModelSaving?: boolean;
      conversationId?: string | null;
   };
   dictation?: ChatInputDictationApi | null;
   enableMemory?: boolean;
   maxRows?: number;
   onExpandedChange?: (expanded: boolean) => void;
   send: {
      show: boolean;
      isSubmitting: boolean;
      onCancelStream?: () => void;
      canSubmit: boolean;
      loading: boolean;
      tooltip?: string;
   };
}>;

/** Stacked composer used on desktop and for the large variant. */
export default function DesktopComposerLayout({
   variant,
   textarea,
   upload,
   tools,
   dictation,
   enableMemory = false,
   maxRows = 12,
   onExpandedChange,
   send,
}: DesktopComposerLayoutProps) {
   const {t: ct} = useTranslation("common");
   const {inputRef, ...textareaProps} = textarea;
   const [expanded, setExpanded] = useState(false);
   const [overflowsOneRow, setOverflowsOneRow] = useState(() => textarea.value.split("\n").length >= 2);
   const showExpand = expanded || overflowsOneRow;
   const expandLabel = expanded ? ct("collapseComposer") : ct("expandComposer");
   const onExpandedChangeRef = useRef(onExpandedChange);
   const wasExpandedRef = useRef(expanded);
   onExpandedChangeRef.current = onExpandedChange;

   const setComposerExpanded = (next: boolean) => {
      setExpanded(next);
      if (next !== expanded) onExpandedChange?.(next);
   };

   useLayoutEffect(() => {
      const el = inputRef.current;
      if (!el) return;

      const update = () => setOverflowsOneRow(countTextareaRows(el) >= 2);
      update();

      if (typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(update);
      observer.observe(el);
      return () => observer.disconnect();
   }, [textarea.value, inputRef]);

   // A single row has no expand control, so an emptied draft can't stay stretched open.
   useLayoutEffect(() => {
      if (!expanded || overflowsOneRow) return;
      setExpanded(false);
      onExpandedChangeRef.current?.(false);
   }, [expanded, overflowsOneRow]);

   useLayoutEffect(() => {
      const el = inputRef.current;
      if (wasExpandedRef.current && !expanded && el) {
         adjustTextareaHeight(el, variant, maxRows);
      }
      wasExpandedRef.current = expanded;
   }, [expanded, variant, maxRows, inputRef]);

   return (
      <div
         className={clsx(
            "relative",
            variant === "compact" && "hidden md:block",
            variant === "large" && "flex flex-col min-h-[inherit]"
         )}
      >
         {showExpand && (
            <Tooltip delayDuration={0}>
               <TooltipTrigger asChild>
                  <button
                     type="button"
                     aria-label={expandLabel}
                     aria-expanded={expanded}
                     onClick={() => {
                        setComposerExpanded(!expanded);
                        inputRef.current?.focus({preventScroll: true});
                     }}
                     className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-background text-500 hover:bg-100 hover:text-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-300"
                  >
                     <Icon
                        icon={
                           expanded
                              ? "down-left-and-up-right-to-center"
                              : "up-right-and-down-left-from-center"
                        }
                        type={ICON_TYPE.LIGHT}
                     />
                  </button>
               </TooltipTrigger>
               <TooltipContent side="bottom">{expandLabel}</TooltipContent>
            </Tooltip>
         )}
         <HighlightedTextarea
            {...textareaProps}
            data-testid="chat-input-desktop"
            data-composer-expanded={expanded ? "true" : undefined}
            ref={inputRef}
            rows={1}
            onKeyDown={(event) => {
               if (event.key === "Escape" && expanded && !textarea.isSlashMenuOpen) {
                  event.preventDefault();
                  setComposerExpanded(false);
                  return;
               }
               textareaProps.onKeyDown?.(event);
            }}
            className={clsx(
               VARIANT_CLASSES.common,
               VARIANT_CLASSES[variant],
               variant === "compact" && "p-3 md:p-4",
               "[&:is(textarea)]:!overflow-y-auto max-h-[min(40dvh,324px)]",
               showExpand && "!pr-14",
               // Leave room for the toolbar and short-window chrome (mobile header,
               // banner). A raw 60dvh field is taller than that column, and the
               // composer shell clips whatever sticks out.
               expanded &&
                  "!h-[min(60dvh,640px,calc(100dvh-16rem))] !max-h-[min(60dvh,640px,calc(100dvh-16rem))]"
            )}
         />

         <div
            className={clsx(
               CONTROLS_CLASSES.common,
               CONTROLS_CLASSES[variant],
               "flex items-center gap-2 mt-auto",
               variant === "compact" && "px-3 py-1 md:px-4 md:py-2",
               upload.show || upload.animating || tools.hasToolsContent ? "justify-between" : "justify-end"
            )}
         >
            <AnimatePresence mode="wait" onExitComplete={upload.onExitComplete}>
               {upload.show && (
                  <motion.div
                     key="upload-button"
                     initial={{opacity: 0, scale: 0.8, y: 10}}
                     animate={{opacity: 1, scale: 1, y: 0}}
                     exit={{opacity: 0, scale: 0.8, y: 10}}
                     transition={{duration: 0.2, ease: "easeInOut"}}
                  >
                     <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                           <button
                              type="button"
                              onClick={upload.onClick}
                              disabled={textarea.disabled || upload.disabled}
                              aria-label={ct("addFiles")}
                              className={clsx(
                                 "h-8 w-8 p-0 rounded-lg bg-transparent text-800 hover:bg-200 disabled:opacity-50 flex items-center justify-center focus:outline-none transition-colors duration-200",
                                 upload.hasAttachedFiles &&
                                    "bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800"
                              )}
                           >
                              <Icon icon="plus" type={ICON_TYPE.LIGHT} />
                           </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">{ct("addFiles")}</TooltipContent>
                     </Tooltip>
                  </motion.div>
               )}
            </AnimatePresence>

            <div className="flex items-center gap-2">
               {tools.shouldShowContextWheel && (
                  <ContextWheel
                     percent={tools.displayedTokenUsagePercent}
                     tokenLimitReached={tools.tokenLimitReached}
                     tokenBreakdown={tools.tokenBreakdown}
                  />
               )}

               <div data-testid="assistant-model-controls">
                  <AssistantModelControls
                     showAssistantSelector={tools.showAssistantSelector}
                     assistants={tools.assistants}
                     selectedAssistant={tools.selectedAssistant}
                     onAssistantChange={tools.onAssistantChange}
                     assistantSelectorDisabled={tools.assistantSelectorDisabled}
                     isLoadingAssistants={tools.isLoadingAssistants}
                     showModelSelector={tools.showModelSelector}
                     onModelChange={tools.onModelChange}
                     canEditModel={tools.canEditModel}
                     isModelSaving={tools.isModelSaving}
                     conversationId={tools.conversationId}
                  />
               </div>

               {enableMemory && <MemoryComposerButton disabled={textarea.disabled} />}

               {dictation && (
                  <DictationButton
                     status={dictation.status}
                     onClick={dictation.toggle}
                     disabled={textarea.disabled && dictation.status !== "recording"}
                  />
               )}

               {send.show && (
                  <SendOrStopButton
                     isSubmitting={send.isSubmitting}
                     onCancelStream={send.onCancelStream}
                     canSubmit={send.canSubmit}
                     loading={send.loading}
                     sendTooltip={send.tooltip}
                  />
               )}
            </div>
         </div>
      </div>
   );
}
